'use client';

import { Button } from '@connected/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@connected/ui/components/dropdown-menu';
import { Link } from '@tanstack/react-router';
import { EllipsisVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { showConfirm } from '@/stores/confirmer';

import type { DeviceActions } from '../../types/hook-types';

import { CopyButton } from '../copy-button';

export type RowProps = {
    electricalProjectVersionId: string;
    electricalProjectDistributionBoardId: string;
    page: number;
};

export type NavItem = {
    label: string;
    to: string;
    params: Record<string, string>;
    search?: Record<string, unknown>;
};

export type RowActionsCellProps = {
    deviceId: string;
    actions: DeviceActions;
    rowProps?: RowProps;
    /**
     * Additional navigation items to show in the dropdown.
     * Used by FCPD to add "Isolatieweerstandmetingen" link.
     */
    extraNavItems?: NavItem[];
};

/**
 * Row actions cell with copy/delete and navigation links.
 * Used in device tables for RCD, RCCB, and FCPD.
 */
export function RowActionsCell({
    deviceId,
    actions,
    rowProps,
    extraNavItems,
}: RowActionsCellProps) {
    const { t: c } = useTranslation('common');

    const handleDelete = async () => {
        if (await showConfirm('Weet je zeker dat je wilt verwijderen?')) {
            await actions.delete(deviceId);
        }
    };

    return (
        <div className="flex justify-end">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                        <EllipsisVertical />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="flex flex-col gap-1">
                    {rowProps && (
                        <DropdownMenuItem asChild>
                            <Link
                                to="/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/devices/$distributionBoardDeviceId/device-observations"
                                params={{
                                    projectVersionId: rowProps.electricalProjectVersionId,
                                    distributionboardId: rowProps.electricalProjectDistributionBoardId,
                                    distributionBoardDeviceId: deviceId,
                                }}
                                search={{
                                    page: rowProps.page,
                                }}
                                className="w-full"
                            >
                                Constateringen
                            </Link>
                        </DropdownMenuItem>
                    )}
                    {extraNavItems?.map(item => (
                        <DropdownMenuItem key={item.to} asChild>
                            <Link
                                to={item.to as any}
                                params={item.params}
                                search={item.search}
                                className="w-full"
                            >
                                {item.label}
                            </Link>
                        </DropdownMenuItem>
                    ))}
                    <CopyButton deviceId={deviceId} onCopy={actions.copy} />
                    <DropdownMenuItem
                        onSelect={handleDelete}
                        className="text-destructive focus:text-destructive"
                    >
                        {c('actions.file.delete')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
